import type {
  userInfoInterface,
  levelExperienceInterface,
  purseInterface,
  bannedStatusInterface
} from '@/types/apiType/userType'
import type { userInfoStorageMapInterface } from '@/types/viewsType/userType'
import {
  apiGetUserInfo,
  apiGetUserLevelExperience,
  apiUpdateLanguage,
  apiGetPurse,
  apiCheckBanned
} from '@/api/user'
import * as Sentry from '@sentry/nuxt'

export const useUserStore = defineStore({
  id: 'user',
  state: () => ({
    userInfo: {} as userInfoInterface, // 用户信息
    userInfoStorageMap: {} as { [uid: string]: userInfoStorageMapInterface }, // 用户信息列表Map，每次获取到用户信息，都要更新一下
    levelExperienceInfo: {} as levelExperienceInterface, // 等级
    purseInfo: {} as purseInterface, // 钱包
    bannedStatusMap: {} as bannedStatusInterface // 封禁状态
  }),
  actions: {
    async getPurseInfo() {
      const res = await apiGetPurse()
      const { code, data } = res
      if (code === 200) {
        this.purseInfo = data
      }
    },
    /**
     * 获取用户等级
     */
    async getLevelExperienceInfo() {
      const res = await apiGetUserLevelExperience({ type: 3 })
      const { code, data } = res
      if (code === 200) {
        this.levelExperienceInfo = data
      }
    },
    /**
     * 获取封禁状态
     */
    async getUserBannedStatus() {
      const res = await apiCheckBanned()
      const { code, data } = res
      if (code === 200) {
        this.bannedStatusMap = data
      }
    },
    /**
     * 用户上线下线调用，后端的自动打招呼功能需要使用，使用localStorage的uid是为了保证退登前使用到,自定打招呼接口暂时注释
     * @param {1 | 2} type // 1上线 2下线
     * */
    async changeManOnline(type: 1 | 2) {
      console.log(type)
      // const loginStr = localStorage.getItem('loginInfo')
      // if (loginStr) {
      //   const loginInfo = JSON.parse(loginStr)
      //   await apiManOnline({ type, uid: loginInfo.uid })
      // }
    },
    // 公共方法，设置指定用户信息
    publicSetUserInfo(info: userInfoStorageMapInterface) {
      // const length = Object.keys(this.userInfoStorageMap).length
      // console.log(length)
      // if (length > 10) {
      //   console.log('已清楚')
      //   this.userInfoStorageMap = {}
      // }
      if (info && info.uid) {
        if (this.userInfoStorageMap[info.uid]) {
          this.userInfoStorageMap[info.uid] = {
            ...this.userInfoStorageMap[info.uid],
            ...info
          }
        } else {
          this.userInfoStorageMap[info.uid] = { ...info }
        }
      }
    },
    // 公共方法获取指定用户信息
    async publicGetUserInfo(queryUid: string | number) {
      const publicStore = usePublicStore()
      const res = await apiGetUserInfo({
        queryUid,
        os: publicStore.isIOSWebView ? 'iOS' : 'web',
        deviceId: publicStore.deviceId
      })
      const { code, data } = res
      if (code === 200) {
        this.publicSetUserInfo(data)
        return Promise.resolve(data as userInfoInterface)
      } else {
        return Promise.reject(false)
      }
    },
    // 更新用户信息列表Map
    // const userInfoStorage = computed(() => userStore.userInfoStorageMap[targetUid.value] as userInfoStorageMapInterface || {})
    updateUserInfoStorageMap(config: {
      type: 'init' | 'put'
      list: userInfoStorageMapInterface[]
    }) {
      switch (config.type) {
        case 'init':
          this.userInfoStorageMap = {}
          break
        case 'put':
          config.list.forEach((v) => {
            this.publicSetUserInfo(v)
          })
          break
      }
    },
    // 不请求接口设置用户数据
    setUserInfo(info: userInfoInterface) {
      const languageStore = useLanguageStore()

      this.userInfo = info
      languageStore.initLanguage() // 初始化语言
      this.publicSetUserInfo(info)
      // sentry监控设置用户信息
      Sentry.setUser({
        username: this.userInfo.nick,
        id: this.userInfo.erbanNo
      })
    },
    /**
     * 请求接口更新数据
     * avatarLoad:boolean 传true就像预加载
     * */
    async requestUserInfo(config?: { avatarLoad?: boolean }) {
      try {
        if (localStorage.getItem('loginInfo')) {
          const publicStore = usePublicStore()
          const queryUid = JSON.parse(
            localStorage.getItem('loginInfo') as string
          ).uid
          this.getPurseInfo() // 获取钱包
          const res = await apiGetUserInfo({
            queryUid,
            os: publicStore.isIOSWebView ? 'iOS' : 'web',
            deviceId: publicStore.deviceId
          })
          // eslint-disable-next-line prefer-const
          let { code, data } = res
          if (code === 200) {
            // 极端特殊情况判断：如果用户没有language，就先配置第一语言再次获取数据。用于用户没有第一语言，一般情况下在登录都会有第一语言的了
            if (!data.firstLanguage) {
              const languageStore = useLanguageStore()
              await apiUpdateLanguage({
                updateLanguage: languageStore.languageInfo.text,
                type: 1
              }) // 配置第一语言
              const resAgain = await apiGetUserInfo({
                queryUid,
                os: publicStore.isIOSWebView ? 'iOS' : 'web',
                deviceId: publicStore.deviceId
              })
              const { code: codeAgain, data: dataAgain } = resAgain
              if (codeAgain === 200) {
                data = dataAgain
              }
            }

            this.setUserInfo(data)

            // 如果需要头像预加载
            if (config?.avatarLoad) {
              new Image().src = common.handlePicUrlAvatar(data.avatar as string)
            }
            return true
          }
          return false
        }
      } catch (error) {
        console.log(error)
      }
    },

    /**
     * APP获取自己用户信息
     * @param avatarLoad:boolean 传true就像预加载
     */
    async requestAppUserInfo(config?: { avatarLoad?: boolean }) {
      return new Promise((resolve) => {
        try {
          if (localStorage.getItem('loginInfo')) {
            const publicStore = usePublicStore()
            const queryUid = JSON.parse(
              localStorage.getItem('loginInfo') as string
            ).uid
            this.getPurseInfo() // 获取钱包

            if (publicStore.checkIOSVersion({ version: '1.3.0' })) {
              // iOS130版本
              window['appFun']('getSelfUserInfo', (data) => {
                this.setUserInfo({
                  nick: data.nick,
                  uid: Number(data.uid),
                  avatar: data.avatar,
                  erbanNo: Number(data.erbanNo),
                  gender: Number(data.gender),
                  experLevel: Number(data.experLevel),
                  charmLevel: Number(data.charmLevel)
                } as userInfoInterface)
                // 如果需要头像预加载
                if (config?.avatarLoad) {
                  new Image().src = common.handlePicUrlAvatar(
                    data.avatar as string
                  )
                }
                // 避免客户端给的信息类型不对，重新请求一遍
                apiGetUserInfo({
                  queryUid,
                  os: publicStore.isIOSWebView ? 'iOS' : 'web',
                  deviceId: publicStore.deviceId
                }).then((res) => {
                  const { code, data } = res
                  if (code === 200) {
                    this.setUserInfo(data)
                  }
                })
                resolve(true)
              })
            } else {
              apiGetUserInfo({
                queryUid,
                os: publicStore.isIOSWebView ? 'iOS' : 'web',
                deviceId: publicStore.deviceId
              })
                .then((res) => {
                  const { code, data } = res
                  if (code === 200) {
                    this.setUserInfo(data)
                    // 如果需要头像预加载
                    if (config?.avatarLoad) {
                      new Image().src = common.handlePicUrlAvatar(
                        data.avatar as string
                      )
                    }
                    resolve(true)
                  } else {
                    resolve(false)
                  }
                })
                .catch(() => {
                  resolve(false)
                })
            }
          }
        } catch (error) {
          console.log(error)
          resolve(false)
        }
      })
    },

    // 重置数据
    resetData() {
      this.userInfo = common.defaultInfo
      this.userInfoStorageMap = {}
      this.levelExperienceInfo = common.defaultLevelExperienceInfo
      this.purseInfo = common.defaultPurseInfo
      // 清除sentry当前设置的用户
      Sentry.setUser(null)
    }
  },
  persist: {
    key: 'userStore',
    paths: [
      'userInfo',
      'userInfoStorageMap',
      'levelExperienceInfo',
      'purseInfo'
    ]
  }
})
